import { DateTime } from '@ui/v2/Table/cells/DateTime'
import { VIEW_MORE_ARROW_COL_KEY } from '@ui/v2/Table/hooks/useTableSearch'
import { BaseColumn, BaseColumnCtx } from '@ui/v2/Table/types'

import { getCompanyName } from '@utils/companyName.utils'
import { formatDate } from '@utils/date.utils'
import { addSwapFreePostfix } from '@utils/text'

import { DealDto } from '@model/DealDto'
import { PlanValuesKeys } from '@model/MultiTierDto'

import { IBIncomeTableSortKeys } from './IBIncomeTable'

export interface IncomeColumnCtx extends BaseColumnCtx {
  formatNumber: (v?: number) => string
}
export type IncomeColumn = BaseColumn<
  DealDto,
  IncomeColumnCtx,
  IBIncomeTableSortKeys | typeof VIEW_MORE_ARROW_COL_KEY
>

export const incomeColumns: IncomeColumn[] = [
  {
    id: 'TradingAccount.Name',
    labelKey: 'Trading Account.Trading Account',
    responsiveWidth: { desktop: { minWidth: 178 } },
    visibleOnMobile: true,
    getValue: ({ tradingAccount }) => tradingAccount.name,
  },
  {
    id: 'TradingAccount.Wallet.Client.Name',
    labelKey: 'IB.Client Name',
    minWidth: 163,
    getValue: ({ client }) => client.name,
  },
  {
    id: 'TradedAt',
    labelKey: 'IB.Date & Time',
    minWidth: 143,
    getValue: ({ tradedAt }, { t, dateFormat }) => {
      const [date, time] = formatDate(tradedAt, { formatType: dateFormat })?.split(' ') ?? ['', '']
      return <DateTime date={date} time={time} t={t} />
    },
  },
  {
    id: 'CurrencyId',
    labelKey: 'IB.Currency',
    minWidth: 90,
    getValue: ({ currency }) => currency.id,
  },
  {
    id: 'TradingAccount.AccountGroupServer.AccountGroup.TradingAccountType.Name',
    labelKey: 'Trading Account.Account Type',
    getValue: ({ isSwapFree, tradingAccountType }, { t }) =>
      addSwapFreePostfix(isSwapFree, tradingAccountType.name, t),
  },
  {
    id: 'InstrumentName',
    labelKey: 'IB.Instrument',
    getValue: ({ instrument }) => instrument.name,
  },
  {
    id: 'TradingAccount.Wallet.Client.TickmillCompany.Name',
    labelKey: 'IB.Entity',
    getValue: ({ tickmillCompany }) =>
      tickmillCompany ? getCompanyName(tickmillCompany.id) : null,
  },
  {
    id: 'Amount',
    labelKey: 'IB.Commission',
    align: 'right',
    isNumeric: true,
    visibleOnMobile: true,
    getValue: ({ amount }, { formatMoney }) => formatMoney?.(amount),
  },
  {
    id: 'Volume',
    labelKey: 'IB.Volume',
    align: 'right',
    isNumeric: true,
    getValue: ({ volume }, { formatNumber }) => formatNumber(volume),
  },
  {
    id: 'SpreadRevenue',
    labelKey: 'IB.Spread Revenue',
    align: 'right',
    isNumeric: true,
    getValue: ({ spreadRevenue, tradingAccountType }, { t, formatMoney }) => {
      const isAccountTypeClassic = PlanValuesKeys.Classic === tradingAccountType.name
      return isAccountTypeClassic ? formatMoney?.(spreadRevenue ?? 0) : t('N/A')
    },
  },
  {
    id: VIEW_MORE_ARROW_COL_KEY,
    labelKey: '',
    visibleOnMobile: true,
    align: 'center',
    width: 40,
    getValue: () => null,
  },
]
